import React, { useEffect, useMemo, useState } from "react";
import { FunnelIcon, ChevronDownIcon, ChevronUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { ButtonInput } from "../Inputs/ButtonInput";
import Modal from "../../../layouts/Modal";


export type SearchByOption = {
  label: string;
  value: string;
};

type SearchByFiltersModalProps = {
  options: SearchByOption[];
  buttonLabel?: string;

  // selected values
  searchBy: string[];
  setSearchBy: React.Dispatch<React.SetStateAction<string[]>>;

  disabled?: boolean;
  isLoading?: boolean;
  applyOnClose?: boolean;
  onOpenChange?: (open: boolean) => void;
  extraContent?: React.ReactNode;

  // optional: modal width (matches your Modal prop)
  modalWidth?: string; // e.g. "sm:max-w-md"
};

export default function SearchByFiltersModal({
  options,
  buttonLabel = "Search By",
  searchBy,
  setSearchBy,
  disabled,
  isLoading = false,
  applyOnClose = false,
  onOpenChange,
  extraContent,
  modalWidth = "sm:max-w-md",
}: SearchByFiltersModalProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [draftSearchBy, setDraftSearchBy] = useState<string[]>(searchBy);

  useEffect(() => {
    if (!open) {
      setDraftSearchBy(searchBy);
    }
  }, [searchBy, open]);

  const selectedValues = applyOnClose ? draftSearchBy : searchBy;
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const toggleValue = (value: string) => {
    if (applyOnClose) {
      setDraftSearchBy((prev) => {
        const set = new Set(prev);
        if (set.has(value)) set.delete(value);
        else set.add(value);
        return Array.from(set);
      });
      return;
    }

    setSearchBy((prev) => {
      const set = new Set(prev);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return Array.from(set);
    });
  };

  const clearAll = () => {
    if (applyOnClose) {
      setDraftSearchBy([]);
      return;
    }
    setSearchBy([]);
  };

  const handleSetOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    setOpen((prev) => {
      const next = typeof value === "function" ? value(prev) : value;

      if (next && !prev && applyOnClose) {
        setDraftSearchBy(searchBy);
      }

      if (!next && prev && applyOnClose) {
        setSearchBy(draftSearchBy);
      }

      onOpenChange?.(next);
      return next;
    });
  };

  return (
    <>
      {/* Filter button */}
      <ButtonInput
        label=""
        disabled={!!disabled}
        isLoading={isLoading}
        isSubmit={false}
        onClick={() => handleSetOpen((v) => !v)}
        classes="px-3 py-2 shadow-sm bg-primary-600 hover:bg-primary-700 disabled:bg-gray-500 disabled:hover:bg-gray-500 !text-white flex items-center gap-2"
      >
        <span className="flex items-center gap-2 text-sm !text-white">
          <FunnelIcon className="h-4 w-4" />
          {t(buttonLabel)}
          {open ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        </span>
      </ButtonInput>

      {/* Modal */}
      <Modal open={open} setOpen={handleSetOpen} width={modalWidth}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{t("Search By")}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("Choose which fields the search box should use.")}
            </p>
          </div>

          <button
            type="button"
            onClick={() => handleSetOpen(false)}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
            aria-label={t("Close")}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <hr className="my-4" />

        {/* Checkbox list */}
        <div className="max-h-[55vh] overflow-auto pr-1">
          <fieldset className="space-y-2">
            {options.map((opt) => {
              const checked = selectedSet.has(opt.value);

              return (
                <label
                  key={opt.value}
                  className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleValue(opt.value)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    disabled={disabled}
                  />
                  <span className="text-sm text-gray-900">{t(opt.label)}</span>
                </label>
              );
            })}
          </fieldset>
        </div>

        {extraContent ? (
          <>
            <hr className="my-4" />
            {extraContent}
          </>
        ) : null}

        {/* Actions */}
        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={clearAll}
            className="text-sm font-medium text-gray-600 hover:text-gray-900"
            disabled={disabled || selectedValues.length === 0}
          >
            {t("Clear")}
          </button>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleSetOpen(false)}
              className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200"
            >
              {t("Done")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
